import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Button,
  LinearProgress,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActions,
  alpha,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Zoom,
  Snackbar,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from "@mui/material";
import { useThemeMode } from "../contexts/ThemeContext";
import apiService, { SubscriptionResponse, PlanChangePreviewResponse } from "../services/api";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningIcon from "@mui/icons-material/Warning";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import RefreshIcon from "@mui/icons-material/Refresh";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { notificationService } from "../services/notificationService";
import { useNavigate } from "react-router-dom";
import StarIcon from "@mui/icons-material/Star";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

type CheckoutLoadingState =
  | null
  | "upgrade"
  | "STARTER_100"
  | "STARTER_200"
  | "STARTER_400"
  | "BUSINESS_800"
  | "BUSINESS_1500"
  | "BUSINESS_2200"
  | "ENTERPRISE_3200"
  | "ENTERPRISE_4500"
  | "ENTERPRISE_7000"
  | "STARTER_ANNUAL_100"
  | "STARTER_ANNUAL_200"
  | "STARTER_ANNUAL_400"
  | "BUSINESS_ANNUAL_800"
  | "BUSINESS_ANNUAL_1500"
  | "BUSINESS_ANNUAL_2200"
  | "ENTERPRISE_ANNUAL_3200"
  | "ENTERPRISE_ANNUAL_4500"
  | "ENTERPRISE_ANNUAL_7000"
  | 100
  | 500
  | 1000;

type PurchaseSeverity = "success" | "info" | "warning" | "error";

type PurchaseBanner = {
  severity: PurchaseSeverity;
  message: string;
};

const PERIOD_TYPE_MONTHLY = 501900000;
const PERIOD_TYPE_YEARLY = 501900001;
const GRACE_PERIOD_DAYS = 3;

interface PlanOption {
  code:
    | "STARTER_100"
    | "STARTER_200"
    | "STARTER_400"
    | "BUSINESS_800"
    | "BUSINESS_1500"
    | "BUSINESS_2200"
    | "ENTERPRISE_3200"
    | "ENTERPRISE_4500"
    | "ENTERPRISE_7000"
    | "STARTER_ANNUAL_100"
    | "STARTER_ANNUAL_200"
    | "STARTER_ANNUAL_400"
    | "BUSINESS_ANNUAL_800"
    | "BUSINESS_ANNUAL_1500"
    | "BUSINESS_ANNUAL_2200"
    | "ENTERPRISE_ANNUAL_3200"
    | "ENTERPRISE_ANNUAL_4500"
    | "ENTERPRISE_ANNUAL_7000";
  title: string;
  price: number;
  pages: number;
  popular?: boolean;
  originalPrice?: number;
}

interface ExtraPagesOption {
  pages: 100 | 500 | 1000;
  price: number;
}

const MONTHLY_PLANS: PlanOption[] = [
  { code: "STARTER_100", title: "Starter", price: 29, pages: 100 },
  { code: "STARTER_200", title: "Starter Plus", price: 39, pages: 200 },
  { code: "STARTER_400", title: "Starter Pro", price: 79, pages: 400 },
  { code: "BUSINESS_800", title: "Business", price: 149, pages: 800 },
  { code: "BUSINESS_1500", title: "Business Plus", price: 229, pages: 1500, popular: true },
  { code: "BUSINESS_2200", title: "Business Pro", price: 299, pages: 2200 },
  { code: "ENTERPRISE_3200", title: "Enterprise", price: 399, pages: 3200 },
  { code: "ENTERPRISE_4500", title: "Enterprise Plus", price: 599, pages: 4500 },
  { code: "ENTERPRISE_7000", title: "Enterprise Pro", price: 899, pages: 7000 }
];

const YEARLY_PLANS: PlanOption[] = [
  { code: "STARTER_ANNUAL_100", title: "Starter", price: 261, pages: 1200, originalPrice: 348 },
  { code: "STARTER_ANNUAL_200", title: "Starter Plus", price: 361, pages: 2400, originalPrice: 468 },
  { code: "STARTER_ANNUAL_400", title: "Starter Pro", price: 711, pages: 4800, originalPrice: 948 },
  { code: "BUSINESS_ANNUAL_800", title: "Business", price: 1341, pages: 9600, originalPrice: 1788 },
  { code: "BUSINESS_ANNUAL_1500", title: "Business Plus", price: 2061, pages: 18000, originalPrice: 2748, popular: true },
  { code: "BUSINESS_ANNUAL_2200", title: "Business Pro", price: 2691, pages: 26400, originalPrice: 3588 },
  { code: "ENTERPRISE_ANNUAL_3200", title: "Enterprise", price: 3591, pages: 38400, originalPrice: 4788 },
  { code: "ENTERPRISE_ANNUAL_4500", title: "Enterprise Plus", price: 5391, pages: 54000, originalPrice: 7188 },
  { code: "ENTERPRISE_ANNUAL_7000", title: "Enterprise Pro", price: 8091, pages: 84000, originalPrice: 10788 }
];

const EXTRA_PAGES_OPTIONS: ExtraPagesOption[] = [
  { pages: 100, price: 35 },
  { pages: 500, price: 125 },
  { pages: 1000, price: 200 }
];

const getPlanName = (plan: number | string | null): string => {
  if (plan === "Free" || plan === null) return "Free";
  const planValue = typeof plan === 'string' ? parseInt(plan, 10) : plan;
  switch (planValue) {
    case 501900000: return "Starter";
    case 501900001: return "Business";
    case 501900002: return "Enterprise";
    case 501900003: return "Free Trial";
    default: return "Plan";
  }
};

const getStatusLabel = (status: string | number | null) => {
  if (status === "Active" || status === 0) return "Active";
  if (status === "Inactive" || status === 1) return "Inactive";
  if (status === "Suspended" || status === 2) return "Suspended";
  return "Unknown";
};

const getStatusColor = (status: string | number | null): "success" | "warning" | "error" | "default" => {
  if (status === "Active" || status === 0) return "success";
  if (status === "Inactive" || status === 1) return "error";
  if (status === "Suspended" || status === 2) return "warning";
  return "default";
};

const getFullPlanTitle = (planCode: string | null): string => {
  if (!planCode) return "Plan";
  const allPlans = [...MONTHLY_PLANS, ...YEARLY_PLANS];
  const plan = allPlans.find(p => p.code === planCode);
  if (plan) return plan.title;
  if (planCode.includes('STARTER')) return "Starter";
  if (planCode.includes('BUSINESS')) return "Business";
  if (planCode.includes('ENTERPRISE')) return "Enterprise";
  return "Free Trial";
};

const calculateRenewalDate = (periodType: number | null | undefined, startDate?: Date): Date => {
  const now = startDate || new Date();
  const effectivePeriodType = isYearlyPeriod(periodType) ? PERIOD_TYPE_YEARLY : PERIOD_TYPE_MONTHLY;
  if (effectivePeriodType === PERIOD_TYPE_YEARLY) {
    return new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
  } else {
    return new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
  }
};

const getDaysUntilReset = (nextResetDate: string | Date): number => {
  const today = new Date();
  const resetDate = typeof nextResetDate === 'string' ? new Date(nextResetDate) : nextResetDate;
  const diffTime = resetDate.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const getResetMessage = (daysUntilReset: number, nextResetDate: string, isInGracePeriod: boolean, isExpired: boolean): string => {
  if (isExpired) return "⚠️ Subscription expired - please renew";
  if (isInGracePeriod) {
    const daysOverdue = Math.abs(daysUntilReset);
    const daysLeft = GRACE_PERIOD_DAYS - daysOverdue;
    return `⚠️ Grace period: ${daysLeft} days left to renew`;
  }
  if (daysUntilReset === 0) return "🔄 Expires today!";
  if (daysUntilReset === 1) return "⚠️ Expires tomorrow";
  if (daysUntilReset < 0) return "⚠️ Overdue - please renew";
  return `Expires in ${daysUntilReset} days (${new Date(nextResetDate).toLocaleDateString()})`;
};

const isYearlyPeriod = (period: number | string | null | undefined): boolean => {
  if (period === null || period === undefined) return false;
  if (typeof period === 'string') return period === 'Yearly';
  return period === PERIOD_TYPE_YEARLY;
};

const isMonthlyPeriod = (period: number | string | null | undefined): boolean => {
  if (period === null || period === undefined) return false;
  if (typeof period === 'string') return period === 'Monthly';
  return period === PERIOD_TYPE_MONTHLY;
};

interface UsageStats {
  baseQuota: number;
  extraPages: number;
  totalAvailable: number;
  used: number;
  reserved: number;
  remaining: number;
  percent: number;
}

interface PlanCardProps {
  plan: PlanOption;
  isCurrentPlan?: boolean;
  isSameTier?: boolean;
  isYearlyToMonthly?: boolean;
  isDifferentTier?: boolean;
  onSelect: () => void;
  loading: boolean;
  period: 'monthly' | 'yearly';
  disabled?: boolean;
  disabledReason?: string;
}

const StatCard: React.FC<{
  label: string;
  value: string | number;
  helper?: string;
  icon?: React.ReactNode;
  emphasis?: boolean;
}> = ({ label, value, helper, icon, emphasis = false }) => {
  const theme = useTheme();
  const { mode } = useThemeMode();

  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: 3,
        background: emphasis
          ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`
          : 'transparent',
        border: `1px solid ${emphasis
          ? theme.palette.secondary.main
          : mode === 'light'
            ? 'rgba(10, 37, 64, 0.08)'
            : 'rgba(255, 255, 255, 0.08)'}`,
        transition: 'transform 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4]
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {label}
          </Typography>
          {icon && <Box sx={{ color: theme.palette.secondary.main }}>{icon}</Box>}
        </Box>
        <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
          {typeof value === "number" ? value.toLocaleString() : value}
        </Typography>
        {helper && (
          <Typography variant="caption" color="text.secondary">
            {helper}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const getNextPlanPages = (planCode: string): number | null => {
  const allPlans = [...MONTHLY_PLANS, ...YEARLY_PLANS];
  const currentPlan = allPlans.find(p => p.code === planCode);
  if (!currentPlan) return null;

  const sameTierPlans = allPlans
    .filter(p =>
      (planCode.includes('STARTER') && p.code.includes('STARTER')) ||
      (planCode.includes('BUSINESS') && p.code.includes('BUSINESS')) ||
      (planCode.includes('ENTERPRISE') && p.code.includes('ENTERPRISE'))
    )
    .sort((a, b) => a.pages - b.pages);

  const currentIndex = sameTierPlans.findIndex(p => p.code === planCode);
  if (currentIndex < sameTierPlans.length - 1) {
    return sameTierPlans[currentIndex + 1].pages;
  }
  return null;
};

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  isCurrentPlan,
  isSameTier,
  isYearlyToMonthly,
  isDifferentTier,
  onSelect,
  loading,
  period,
  disabled,
  disabledReason
}) => {
  const theme = useTheme();
  const { mode } = useThemeMode();

  const getButtonText = () => {
    if (isCurrentPlan) return '✓ Current Plan';
    if (isYearlyToMonthly) return '🔒 Not Available';
    if (isDifferentTier) return 'Switch Plan';
    return loading ? 'Processing...' : 'Select Plan';
  };

  const getStatementCount = () => {
    const avgPagesPerStatement = 30;
    const statementsPerPeriod = Math.floor(plan.pages / avgPagesPerStatement);
    if (statementsPerPeriod <= 0) return '';
    return period === 'monthly' 
      ? `≈ ${statementsPerPeriod} bank statements/month`
      : `≈ ${statementsPerPeriod} bank statements/year`;
  };

  const getRetentionDays = () => {
    if (plan.code.includes('STARTER')) return '30 days';
    if (plan.code.includes('BUSINESS')) return '120 days';
    if (plan.code.includes('ENTERPRISE')) return '365 days';
    return '30 days';
  };

  return (
    <Zoom in={true} style={{ transitionDelay: '100ms' }}>
      <Card
        sx={{
          position: 'relative',
          borderRadius: 4,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          border: `2px solid ${isCurrentPlan
              ? theme.palette.success.main
              : isYearlyToMonthly
                ? theme.palette.warning.main
                : isSameTier
                  ? alpha(theme.palette.info.main, 0.5)
                  : plan.popular
                    ? theme.palette.secondary.main
                    : mode === 'light'
                      ? 'rgba(10, 37, 64, 0.08)'
                      : 'rgba(255, 255, 255, 0.08)'
            }`,
          background: isCurrentPlan
            ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`
            : plan.popular
              ? `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.03)} 0%, ${alpha(theme.palette.secondary.light, 0.03)} 100%)`
              : isSameTier
                ? `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.02)} 0%, ${alpha(theme.palette.info.light, 0.02)} 100%)`
                : 'transparent',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': disabled ? {} : {
            transform: 'translateY(-8px)',
            boxShadow: theme.shadows[12],
            borderColor: theme.palette.secondary.main
          }
        }}
      >
        {plan.popular && !isCurrentPlan && !isSameTier && !disabled && (
          <Box sx={{ 
            position: 'absolute', 
            top: 12, 
            right: 20, 
            bgcolor: theme.palette.secondary.main, 
            color: '#0A1E3C', 
            px: 1.5, 
            py: 0.5, 
            borderRadius: 2, 
            fontSize: '0.7rem', 
            fontWeight: 800, 
            textTransform: 'uppercase', 
            letterSpacing: '0.5px', 
            zIndex: 10, 
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.3)}` 
          }}>
            <StarIcon sx={{ fontSize: 14 }} /> Best Value
          </Box>
        )}
        {isCurrentPlan && (
          <Box sx={{ 
            position: 'absolute', 
            top: 12, 
            right: 20, 
            bgcolor: theme.palette.success.main, 
            color: 'white', 
            px: 1.5, 
            py: 0.5, 
            borderRadius: 2, 
            fontSize: '0.7rem', 
            fontWeight: 800, 
            textTransform: 'uppercase', 
            letterSpacing: '0.5px', 
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}>
            <CheckCircleIcon sx={{ fontSize: 14 }} /> Active
          </Box>
        )}
        <CardContent sx={{ p: 3, flex: 1 }}>
          <Typography variant="h5" fontWeight={800} gutterBottom>{plan.title}</Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2, flexWrap: 'wrap' }}>
            {plan.originalPrice && (
              <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through', mr: 1, fontSize: '1rem' }}>
                ${plan.originalPrice}
              </Typography>
            )}
            <Typography variant="h3" fontWeight={900} color="primary" sx={{ fontSize: { xs: '2rem', sm: '2.5rem' } }}>
              ${plan.price}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              / {period === 'monthly' ? 'month' : 'year'}
            </Typography>
          </Box>
          
          {plan.originalPrice && (
            <Chip 
              label="🔥 Save 25%" 
              size="small" 
              sx={{ 
                mb: 2, 
                bgcolor: alpha(theme.palette.success.main, 0.15), 
                color: theme.palette.success.main, 
                fontWeight: 700,
                fontSize: '0.7rem'
              }} 
            />
          )}
          
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CheckCircleIcon sx={{ color: theme.palette.secondary.main, fontSize: 18 }} />
              <Typography variant="body2">
                <strong>{plan.pages.toLocaleString()}</strong> pages per {period === 'monthly' ? 'month' : 'year'}
              </Typography>
            </Box>
            
            {getStatementCount() && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <SpeedIcon sx={{ color: theme.palette.info.main, fontSize: 18 }} />
                <Typography variant="body2" color="text.secondary">
                  {getStatementCount()}
                </Typography>
              </Box>
            )}
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityIcon sx={{ color: theme.palette.info.main, fontSize: 18 }} />
              <Typography variant="body2" color="text.secondary">
                Data retention: <strong>{getRetentionDays()}</strong> after expiry
              </Typography>
            </Box>
          </Box>
        </CardContent>
        
        <CardActions sx={{ p: 3, pt: 0 }}>
          <Tooltip title={disabledReason || ''} arrow disableHoverListener={!disabled}>
            <span style={{ width: '100%' }}>
              <Button 
                fullWidth 
                variant={isCurrentPlan ? "outlined" : "contained"} 
                onClick={onSelect} 
                disabled={loading || isCurrentPlan || disabled}
                sx={{ 
                  py: 1.2,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  ...(isCurrentPlan 
                    ? { 
                        borderColor: theme.palette.success.main, 
                        color: theme.palette.success.main,
                        '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.05) }
                      } 
                    : isYearlyToMonthly 
                      ? { 
                          bgcolor: alpha(theme.palette.warning.main, 0.1), 
                          color: theme.palette.warning.main,
                          border: `1px solid ${theme.palette.warning.main}`
                        } 
                      : isSameTier 
                        ? { 
                            background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.light} 100%)`, 
                            color: 'white' 
                          } 
                        : { 
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, 
                            color: 'white',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: theme.shadows[4]
                            }
                          }
                  )
                }}
              >
                {getButtonText()}
              </Button>
            </span>
          </Tooltip>
        </CardActions>
      </Card>
    </Zoom>
  );
};

const SubscriptionPage: React.FC = () => {
  const theme = useTheme();
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const [data, setData] = useState<SubscriptionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<CheckoutLoadingState>(null);
  const [pageBanner, setPageBanner] = useState<PurchaseBanner | null>(null);
  const [period, setPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [renewalDate, setRenewalDate] = useState<Date | null>(null);
  const [basePlanCode, setBasePlanCode] = useState<string | null>(null);
  const [nextResetDate, setNextResetDate] = useState<string | null>(null);
  const [subscriptionStartDate, setSubscriptionStartDate] = useState<string | null>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupSeverity, setPopupSeverity] = useState<"success" | "info" | "warning" | "error">("info");
  const [autoRenewUpdating, setAutoRenewUpdating] = useState(false);
  const [autoRenewConfirmOpen, setAutoRenewConfirmOpen] = useState(false);
  const [pendingAutoRenewValue, setPendingAutoRenewValue] = useState<boolean | null>(null);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [upgradePreviewLoading, setUpgradePreviewLoading] = useState(false);
  const [upgradePreview, setUpgradePreview] = useState<PlanChangePreviewResponse | null>(null);
  const [selectedUpgradePlan, setSelectedUpgradePlan] = useState<PlanOption | null>(null);

  const hasRunInitialLoad = useRef(false);
  const planSectionRef = useRef<HTMLDivElement | null>(null);
  const prevSubscriptionRef = useRef<SubscriptionResponse | null>(null);
  const sentNotificationsRef = useRef<Set<string>>(new Set());
  const isInitialLoadRef = useRef<boolean>(true);

  useEffect(() => {
    if (data) {
      const isYearly = isYearlyPeriod(data.period);
      console.log(`📅 Setting period to: ${isYearly ? 'yearly' : 'monthly'} based on data.period: ${data.period}`);
      setPeriod(isYearly ? 'yearly' : 'monthly');
      
      if (data.nextResetDate) setNextResetDate(data.nextResetDate);
      if (data.subscriptionStartDate) {
        setSubscriptionStartDate(data.subscriptionStartDate);
        setRenewalDate(calculateRenewalDate(data.period, new Date(data.subscriptionStartDate)));
      }
    }
  }, [data]);

  const loadSubscription = useCallback(async () => {
    const res = await apiService.getSubscription();
    setData(res);
    
    if (res?.basePlanCode) {
      setBasePlanCode(res.basePlanCode);
      console.log(`📋 Base plan code from API: ${res.basePlanCode}`);
    } else if (res?.plan) {
      const isYearly = isYearlyPeriod(res.period);
      const monthlyLimit = res.monthlyLimit || 0;
      const plans = isYearly ? YEARLY_PLANS : MONTHLY_PLANS;
      const matchedPlan = plans.find(p => p.pages === monthlyLimit);
      if (matchedPlan) {
        setBasePlanCode(matchedPlan.code);
        console.log(`📋 Determined base plan code: ${matchedPlan.code} from pages: ${monthlyLimit}`);
      }
    }
    return res;
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try { await loadSubscription(); }
    catch (err) { console.error("Subscription load failed", err); setPageBanner({ severity: "error", message: "Failed to load subscription details." }); }
    finally { setLoading(false); }
  }, [loadSubscription]);

  const usage: UsageStats = useMemo(() => {
    if (!data) return { baseQuota: 0, extraPages: 0, totalAvailable: 0, used: 0, reserved: 0, remaining: 0, percent: 0 };
    const baseQuota = Number(data.monthlyLimit ?? 0);
    const extraPages = Number(data.extraPages ?? 0);
    const totalAvailable = Number(data.totalAvailable ?? baseQuota + extraPages);
    const used = Number(data.pagesUsed ?? 0);
    const reserved = Number(data.pagesReserved ?? 0);
    const remaining = Number(data.pagesRemaining ?? 0);
    const percent = totalAvailable > 0 ? Math.min((used / totalAvailable) * 100, 100) : 0;
    return { baseQuota, extraPages, totalAvailable, used, reserved, remaining, percent };
  }, [data]);

  const getProgressColor = (): string => {
    const percent = usage.percent;
    if (percent >= 90) return theme.palette.error.main;
    if (percent >= 70) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const getRecommendedUpgradePlanCode = (): { code: PlanOption["code"]; name: string; pages: number } => {
    const currentPeriod = data?.period;
    const isYearly = isYearlyPeriod(currentPeriod);
    const availablePlans = isYearly ? YEARLY_PLANS : MONTHLY_PLANS;
    const currentPages = data?.monthlyLimit || 0;
    
    if (currentPages === 5 || data?.basePlanCode === "FREE_TRIAL") {
      const starterPlan = availablePlans.find(p => 
        p.code === (isYearly ? "STARTER_ANNUAL_100" : "STARTER_100")
      );
      if (starterPlan) {
        return { code: starterPlan.code, name: starterPlan.title, pages: starterPlan.pages };
      }
    }
    
    let currentIndex = availablePlans.findIndex(p => p.pages === currentPages);
    
    if (currentIndex === -1 && basePlanCode) {
      currentIndex = availablePlans.findIndex(p => p.code === basePlanCode);
    }
    
    if (currentIndex === -1) {
      console.warn(`No matching plan found for pages: ${currentPages}`);
      return { code: availablePlans[0].code, name: availablePlans[0].title, pages: availablePlans[0].pages };
    }
    
    if (currentIndex === availablePlans.length - 1) {
      console.log(`📊 At max plan: ${availablePlans[currentIndex].title}`);
      return { code: availablePlans[currentIndex].code, name: availablePlans[currentIndex].title, pages: availablePlans[currentIndex].pages };
    }
    
    const nextPlan = availablePlans[currentIndex + 1];
    console.log(`📊 Recommended upgrade: ${nextPlan.title} (${nextPlan.pages} pages)`);
    return { code: nextPlan.code, name: nextPlan.title, pages: nextPlan.pages };
  };

  const waitForConfirmedPlan = async (targetPlanCode: string): Promise<boolean> => {
    for (let attempt = 0; attempt < 12; attempt++) {
      const latest = await loadSubscription();
      if (latest?.basePlanCode === targetPlanCode) return true;
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }
    return false;
  };

  const handlePlanSelect = async (planCode: PlanOption["code"]) => {
    const selected = [...MONTHLY_PLANS, ...YEARLY_PLANS].find((plan) => plan.code === planCode) || null;
    // Only use the in-place Stripe plan-change flow when the customer has a
    // currently active paid subscription. Expired/inactive customers start a
    // fresh Checkout subscription instead.
    const hasExistingSubscription =
      data?.isPaidStripeSubscription === true && data?.isActive === true;

    if (!hasExistingSubscription) {
      await startPlanCheckout(planCode);
      return;
    }

    try {
      setUpgradePreviewLoading(true);
      setSelectedUpgradePlan(selected);
      const preview = await apiService.previewPlanChange({ planCode });
      setUpgradePreview(preview);
      setUpgradeDialogOpen(true);
    } catch (error: any) {
      const code = error?.response?.data?.code || "";
      const message = error?.response?.data?.error || error?.message || "Unable to preview this plan change.";
      setPopupSeverity(code.includes("DOWNGRADE") || code.includes("YEARLY_TO_MONTHLY") ? "warning" : "error");
      setPopupMessage(message);
      setPopupOpen(true);
    } finally {
      setUpgradePreviewLoading(false);
    }
  };

  const confirmPlanChange = async () => {
    if (!selectedUpgradePlan) return;
    try {
      setCheckoutLoading(selectedUpgradePlan.code);
      setUpgradeDialogOpen(false);
      const result = await apiService.changePlan({
        planCode: selectedUpgradePlan.code,
        prorationDate: upgradePreview?.prorationDate
      });

      if ((result.actionRequired || result.paymentMethodRequired) && result.hostedInvoiceUrl) {
        window.location.assign(result.hostedInvoiceUrl);
        return;
      }

      setPageBanner({ severity: "info", message: "Payment submitted securely. Confirming your upgraded plan..." });
      const confirmed = await waitForConfirmedPlan(selectedUpgradePlan.code);
      await loadAll();

      if (confirmed) {
        setPageBanner({ severity: "success", message: `${selectedUpgradePlan.title} is now active. Your updated page allowance is ready.` });
        notificationService.addNotification({
          type: "success",
          title: "🚀 Plan upgraded",
          message: `Your ${selectedUpgradePlan.title} plan is now active.`,
          action: { label: "View Plan", link: "/subscription" }
        });
      } else {
        setPageBanner({ severity: "info", message: "Stripe is still confirming the payment. Your existing plan remains active until confirmation completes." });
      }
    } catch (error: any) {
      const code = error?.response?.data?.code || "";
      const message = error?.response?.data?.error || error?.message || "Failed to change plan.";
      setPopupSeverity(code.includes("DOWNGRADE") || code.includes("YEARLY_TO_MONTHLY") ? "warning" : "error");
      setPopupMessage(message);
      setPopupOpen(true);
    } finally {
      setCheckoutLoading(null);
      setUpgradePreview(null);
      setSelectedUpgradePlan(null);
    }
  };

  const startPlanCheckout = async (planCode: PlanOption["code"]) => {
    try {
      setCheckoutLoading(planCode);
      const origin = window.location.origin;
      const result = await apiService.createCheckoutSession({
        checkoutType: "plan", planCode,
        successUrl: `${origin}/subscription?purchase=success&type=plan&planCode=${encodeURIComponent(planCode)}`,
        cancelUrl: `${origin}/subscription?purchase=cancelled&type=plan&planCode=${encodeURIComponent(planCode)}`
      });
      window.location.href = result.url;
    } catch (error) {
      console.error("Failed to create plan checkout:", error);
      const message = (error as any)?.response?.data?.error || "Failed to start plan checkout. Please try again.";
      setPageBanner({ severity: "error", message });
      setCheckoutLoading(null);
    }
  };

  const handleUpgradePlan = async () => {
    if (planSectionRef.current) {
      planSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    // Expired/inactive customers should choose the plan they want rather than
    // being sent directly into a recommended upgrade checkout.
    if (data?.isActive !== true) return;

    const recommended = getRecommendedUpgradePlanCode();

    // Previewing a plan must not use checkoutLoading. That state is reserved for
    // the confirmed Stripe operation; otherwise the confirmation dialog opens
    // already disabled and incorrectly shows "Processing...".
    await handlePlanSelect(recommended.code);
  };

  const handleBuyExtraPages = async (pages: 100 | 500 | 1000) => {
    // Extra pages extend an active subscription. Preserve any existing paid
    // extra-page balance while inactive, but require re-subscription before
    // allowing another top-up purchase.
    if (!hasActiveSubscription) {
      setPopupMessage("An active subscription is required before purchasing additional pages. Select a subscription plan below to reactivate processing.");
      setPopupSeverity("info");
      setPopupOpen(true);
      return;
    }

    try {
      setCheckoutLoading(pages);
      const origin = window.location.origin;
      const result = await apiService.createCheckoutSession({
        checkoutType: "extra_pages", extraPages: pages,
        successUrl: `${origin}/subscription?purchase=success&type=extra_pages&qty=${pages}`,
        cancelUrl: `${origin}/subscription?purchase=cancelled&type=extra_pages&qty=${pages}`
      });
      window.location.href = result.url;
    } catch (error) {
      console.error("Failed to create extra pages checkout:", error);
      setPageBanner({ severity: "error", message: "Failed to start checkout. Please try again." });
      setCheckoutLoading(null);
    }
  };

  const handleRefresh = async () => { await loadAll(); };

  const requestAutoRenewChange = (enabled: boolean) => {
    if (!data?.isPaidStripeSubscription || autoRenewUpdating) return;
    setPendingAutoRenewValue(enabled);
    setAutoRenewConfirmOpen(true);
  };

  const handleAutoRenewChange = async () => {
    if (!data?.isPaidStripeSubscription || pendingAutoRenewValue === null) return;

    const enabled = pendingAutoRenewValue;
    try {
      setAutoRenewUpdating(true);
      const result = await apiService.updateAutoRenew(enabled);
      setData((current) => current ? {
        ...current,
        autoRenew: result.autoRenew,
        cancelAtPeriodEnd: result.cancelAtPeriodEnd,
        stripeSubscriptionStatus: result.stripeSubscriptionStatus,
        stripePeriodStart: result.stripePeriodStart,
        stripePeriodEnd: result.stripePeriodEnd,
        nextResetDate: result.stripePeriodEnd || current.nextResetDate
      } : current);
      setNextResetDate(result.stripePeriodEnd || nextResetDate);

      const effectiveDate = result.stripePeriodEnd
        ? new Date(result.stripePeriodEnd).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
        : 'the end of the current billing period';

      setPopupSeverity("success");
      setPopupMessage(
        result.autoRenew
          ? `Auto-Renew enabled successfully. Your subscription will renew automatically on ${effectiveDate}.`
          : `Auto-Renew disabled successfully. Your subscription remains active until ${effectiveDate}. No further payments will be taken.`
      );
      setPopupOpen(true);
      setAutoRenewConfirmOpen(false);
      setPendingAutoRenewValue(null);
    } catch (error: any) {
      console.error("Failed to update auto-renew:", error);
      setPopupSeverity("error");
      setPopupMessage(
        error?.response?.data?.error ||
        "Unable to update Auto-Renew. Please try again."
      );
      setPopupOpen(true);
    } finally {
      setAutoRenewUpdating(false);
    }
  };

  const daysUntilReset = nextResetDate ? getDaysUntilReset(nextResetDate) : null;
  const resetMessage = nextResetDate ? getResetMessage(daysUntilReset!, nextResetDate, data?.isInGracePeriod || false, data?.isExpired || false) : null;

  useEffect(() => {
    if (hasRunInitialLoad.current) return;
    hasRunInitialLoad.current = true;
    let cancelled = false;
    const run = async () => {
      try {
        const initialSubscription = await loadAll() as unknown as SubscriptionResponse | null;
        if (cancelled) return;
        const params = new URLSearchParams(window.location.search);
        const purchase = params.get("purchase");
        const purchaseType = params.get("type");
        const sessionId = params.get("session_id");
        if (!purchase) return;
        // Remove Checkout query parameters immediately so stale success banners cannot reappear.
        window.history.replaceState({}, "", window.location.pathname);
        if (purchase === "cancelled") {
          setPageBanner({ severity: "info", message: purchaseType === "plan" ? "Plan checkout was cancelled." : "Purchase was cancelled." });
          return;
        }
        if (purchase !== "success") return;

if (sessionId) {
  const purchaseStorageKey = `extractfast_purchase_${sessionId}`;
  const purchaseEventState = sessionStorage.getItem(purchaseStorageKey);

  if (!purchaseEventState) {
    sessionStorage.setItem(purchaseStorageKey, "processing");

    try {
      const order = await apiService.getStripeOrder(sessionId);

      if (order.paymentConfirmed) {
        const analyticsWindow = window as typeof window & {
          dataLayer?: Array<Record<string, unknown>>;
        };

        analyticsWindow.dataLayer =
          analyticsWindow.dataLayer || [];

        analyticsWindow.dataLayer.push({
          event: "purchase",
          ecommerce: {
            transaction_id: order.transaction_id,
            value: order.value,
            currency: order.currency,
            items: order.items,
          },
          checkout_type: order.checkout_type,
          plan_code: order.plan_code,
          extra_pages: order.extra_pages,
        });

        sessionStorage.setItem(purchaseStorageKey, "sent");
      } else {
        sessionStorage.removeItem(purchaseStorageKey);
      }
    } catch (error) {
      sessionStorage.removeItem(purchaseStorageKey);

      console.error(
        "Stripe purchase analytics verification failed",
        error
      );
    }
  } else {
    console.info(
      `Purchase event already ${purchaseEventState} for Stripe session ${sessionId}.`
    );
  }
} else {
  console.warn(
    "Stripe success return did not include session_id."
  );
}

setPageBanner({
  severity: "info",
  message: "Payment received. Refreshing your balance...",
});
        setPageBanner({ severity: "info", message: "Payment received. Refreshing your balance..." });
        
        const initialExtraPages = Number(initialSubscription?.extraPages ?? 0);
        const initialPlan = initialSubscription?.plan;
        const initialPlanCode = initialSubscription?.basePlanCode;
        const initialStatus = initialSubscription?.status;
        
        const maxAttempts = 12;
        const delayMs = 1500;
        const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
        
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          if (cancelled) return;
          try {
            const latestSubscription = await loadSubscription();
            const latestExtraPages = Number(latestSubscription?.extraPages ?? 0);
            const latestPlanCode = latestSubscription?.basePlanCode;
            const latestPlanValue = latestSubscription?.plan;
            const latestStatus = latestSubscription?.status;
            
            console.log(`🔍 Poll attempt ${attempt + 1}/${maxAttempts}`);
            console.log(`🔍 initialPlanCode=${initialPlanCode}, latestPlanCode=${latestPlanCode}`);
            console.log(`🔍 initialPlan=${initialPlan}, latestPlan=${latestPlanValue}`);
            console.log(`🔍 initialStatus=${initialStatus}, latestStatus=${latestStatus}`);
            
            if (purchaseType === "extra_pages") {
              if (latestExtraPages !== initialExtraPages) {
                setPageBanner({ severity: "success", message: "Payment confirmed. Your updated page balance is now shown below." });
                      notificationService.addNotification({
                  type: 'success', title: '✅ Extra Pages Added',
                  message: `You now have ${latestExtraPages} total extra pages.`,
                  action: { label: 'View Usage', link: '/subscription' }
                });
                return;
              }
            } else if (purchaseType === "plan") {
              const planCodeChanged = initialPlanCode && latestPlanCode && initialPlanCode !== latestPlanCode && latestPlanCode !== "FREE_TRIAL";
              const planValueChanged = initialPlan && latestPlanValue && initialPlan !== latestPlanValue;
              const subscriptionActivated = latestStatus === 0 || latestStatus === "Active";
              const wasNotActive = initialStatus !== 0 && initialStatus !== "Active";
              
              if (planCodeChanged || planValueChanged || (subscriptionActivated && wasNotActive)) {
                const planName = getFullPlanTitle(latestPlanCode || '');
                setPageBanner({ severity: "success", message: "Payment confirmed. Your subscription details have been refreshed." });
                      
                if (initialPlan || initialPlanCode) {
                  notificationService.addNotification({ 
                    type: 'success', 
                    title: '🚀 Plan Upgraded', 
                    message: `You've upgraded to ${planName}!`, 
                    action: { label: 'View Plan', link: '/subscription' } 
                  });
                } else {
                  notificationService.addNotification({ 
                    type: 'success', 
                    title: '🎉 Welcome to ExtractFast!', 
                    message: `Your ${planName} plan is now active.`, 
                    action: { label: 'Start Processing', link: '/' } 
                  });
                }
                return;
              }
              
              if (attempt >= 5 && latestPlanCode && latestPlanCode !== "FREE_TRIAL") {
                const planName = getFullPlanTitle(latestPlanCode);
                setPageBanner({ severity: "success", message: "Payment confirmed. Your subscription has been updated." });
                      notificationService.addNotification({ 
                  type: 'success', 
                  title: '🚀 Plan Updated', 
                  message: `You're on the ${planName} plan!`, 
                  action: { label: 'View Plan', link: '/subscription' } 
                });
                return;
              }
            }
            
            if (attempt < maxAttempts - 1) await wait(delayMs);
          } catch (err) { 
            console.error("Post-payment confirmation polling failed", err); 
          }
        }
        
        setPageBanner({ severity: "info", message: "Payment is likely completed. Your balance may take a few more seconds to refresh." });
      } catch (err) { 
        console.error("Subscription load failed", err); 
        setPageBanner({ severity: "error", message: "Failed to load subscription details." }); 
      }
    };
    run();
    return () => { cancelled = true; };
  }, [loadAll, loadSubscription]);

  useEffect(() => {
    const current = data;
    if (!current) return;
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      prevSubscriptionRef.current = current;
      return;
    }
    const prev = prevSubscriptionRef.current;
    if (!prev && current && !sentNotificationsRef.current.has('welcome')) {
      if (current.isActive && !current.isInGracePeriod && !current.isExpired) {
        const planName = getFullPlanTitle(current.basePlanCode || '');
        notificationService.addNotification({ type: 'success', title: '🎉 Welcome to ExtractFast!', message: `Your ${planName} plan is now active.`, action: { label: 'Start Processing', link: '/' } });
        sentNotificationsRef.current.add('welcome');
      }
    }
    if (prev && current) {
      if (prev.monthlyLimit < current.monthlyLimit && !sentNotificationsRef.current.has(`upgrade_${current.monthlyLimit}`)) {
        const addedPages = current.monthlyLimit - prev.monthlyLimit;
        const planName = getFullPlanTitle(current.basePlanCode || '');
        notificationService.addNotification({ type: 'success', title: '🚀 Plan Upgraded', message: `You've upgraded to ${planName}! +${addedPages} pages added.`, action: { label: 'View Plan', link: '/subscription' } });
        sentNotificationsRef.current.add(`upgrade_${current.monthlyLimit}`);
      }
      const prevExtra = prev.extraPages || 0;
      const currentExtra = current.extraPages || 0;
      if (currentExtra > prevExtra && !sentNotificationsRef.current.has(`extra_${currentExtra}`)) {
        const addedPages = currentExtra - prevExtra;
        notificationService.addNotification({ type: 'success', title: '✅ Extra Pages Added', message: `${addedPages} extra pages added. You now have ${currentExtra} total extra pages.`, action: { label: 'View Usage', link: '/subscription' } });
        sentNotificationsRef.current.add(`extra_${currentExtra}`);
      }
      if (!prev.isInGracePeriod && current.isInGracePeriod && !sentNotificationsRef.current.has('grace_period')) {
        notificationService.addNotification({ type: 'error', title: '⚠️ Payment Overdue', message: `Your subscription payment is overdue. You have ${current.gracePeriodRemaining} days left.`, action: { label: 'Renew Now', link: '/subscription' } });
        sentNotificationsRef.current.add('grace_period');
      }
      if (!prev.isExpired && current.isExpired && !sentNotificationsRef.current.has('expired')) {
        notificationService.addNotification({ type: 'error', title: '❌ Subscription Expired', message: 'Your subscription has expired. Please renew.', action: { label: 'Renew Now', link: '/subscription' } });
        sentNotificationsRef.current.add('expired');
      }
      if (!prev.isActive && current.isActive && prev.isExpired && !sentNotificationsRef.current.has('reactivated')) {
        notificationService.addNotification({ type: 'success', title: '✅ Account Reactivated', message: 'Your subscription has been reactivated. Welcome back!', action: { label: 'Start Processing', link: '/' } });
        sentNotificationsRef.current.add('reactivated');
      }
    }
    prevSubscriptionRef.current = current;
  }, [data]);

  const prevUsageRef = useRef<number>(0);
  useEffect(() => {
    if (!data || !data.isActive) return;
    const usagePercent = data.usagePercent || 0;
    const prevPercent = prevUsageRef.current;
    if (prevPercent === 0 && usagePercent === 0) { prevUsageRef.current = usagePercent; return; }
    if (prevPercent < 80 && usagePercent >= 80 && usagePercent < 90) {
      notificationService.addNotification({ type: 'warning', title: '📊 Page Limit Warning', message: `You've used ${Math.round(usagePercent)}% of your page limit. Consider upgrading.`, action: { label: 'View Plans', link: '/subscription' } });
    } else if (prevPercent < 90 && usagePercent >= 90 && usagePercent < 95) {
      notificationService.addNotification({ type: 'warning', title: '⚠️ High Usage Alert', message: `You've used ${Math.round(usagePercent)}% of your page limit. Consider upgrading.`, action: { label: 'Upgrade Plan', link: '/subscription' } });
    } else if (prevPercent < 95 && usagePercent >= 95) {
      notificationService.addNotification({ type: 'error', title: '🔥 Critical Usage Alert', message: `You've used ${Math.round(usagePercent)}% of your page limit. Upgrade now!`, action: { label: 'Upgrade Now', link: '/subscription' } });
    }
    prevUsageRef.current = usagePercent;
  }, [data?.usagePercent]);

  const prevDaysRef = useRef<number>(0);
  useEffect(() => {
    if (!data || !data.isActive || data.isInGracePeriod || data.isExpired) return;
    const daysRemaining = data.daysRemaining || 0;
    const prevDays = prevDaysRef.current;
    if (prevDays === 0 && daysRemaining === 0) { prevDaysRef.current = daysRemaining; return; }
    if (prevDays > 7 && daysRemaining <= 7 && daysRemaining > 3) {
      notificationService.addNotification({ type: 'warning', title: '📅 Plan Expiring Soon', message: `Your plan expires in ${daysRemaining} days. Renew to avoid interruption.`, action: { label: 'Renew Now', link: '/subscription' } });
    } else if (prevDays > 3 && daysRemaining <= 3 && daysRemaining > 1) {
      notificationService.addNotification({ type: 'warning', title: '⚠️ Plan Expiring Soon', message: `Your plan expires in ${daysRemaining} days. Please renew.`, action: { label: 'Renew Now', link: '/subscription' } });
    } else if (prevDays > 1 && daysRemaining === 1) {
      notificationService.addNotification({ type: 'error', title: '🔴 Plan Expires Tomorrow', message: `Your plan expires tomorrow! Renew today.`, action: { label: 'Renew Now', link: '/subscription' } });
    }
    prevDaysRef.current = daysRemaining;
  }, [data?.daysRemaining]);

  if (loading) return <Container sx={{ mt: 6, textAlign: "center" }}><CircularProgress /></Container>;
  if (!data) return <Container sx={{ mt: 6 }}><Alert severity="error">Subscription not found.</Alert></Container>;

  const lowQuotaWarning = data.isActive === true && usage.remaining > 0 && usage.percent >= 70 && usage.percent < 90;
  const exhaustedQuota = data.isActive === true && usage.remaining <= 0;
  const isYearlyPlan = isYearlyPeriod(data?.period);
  const hasActiveSubscription = data?.isActive === true;
  const currentPlanName = basePlanCode ? getFullPlanTitle(basePlanCode) : getPlanName(data.plan);
  const recommendedPlan = getRecommendedUpgradePlanCode();
  const isAtMaxPlan = (() => {
    const currentPeriod = data?.period;
    const isYearly = isYearlyPeriod(currentPeriod);
    const availablePlans = isYearly ? YEARLY_PLANS : MONTHLY_PLANS;
    const currentPages = data?.monthlyLimit || 0;
    const lastPlan = availablePlans[availablePlans.length - 1];
    return currentPages >= lastPlan.pages;
  })();
  
  const progressColor = getProgressColor();
  const progressLightColor = usage.percent >= 90 ? theme.palette.error.light : usage.percent >= 70 ? theme.palette.warning.light : theme.palette.success.light;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={800} sx={{ background: "linear-gradient(135deg, #0A2540 0%, #1E3A5F 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", mb: 1 }}>Subscription Management</Typography>
        <Typography variant="body1" color="text.secondary">Manage your plan, monitor usage, and purchase additional pages</Typography>
      </Box>

      <Paper sx={{ p: { xs: 2.25, md: 2.5 }, mb: 4, borderRadius: 3, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`, border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}` }}>
        <Grid container spacing={{ xs: 2, md: 2.5 }} alignItems="center">
          <Grid size={{ xs: 12, md: 7 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: alpha(theme.palette.secondary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}><LocalOfferIcon sx={{ color: theme.palette.secondary.main }} /></Box>
              <Box>
                <Typography variant="overline" color="text.secondary">Current Plan</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h5" fontWeight={800}>{currentPlanName}</Typography>
                  <Chip label={getStatusLabel(data.status)} color={getStatusColor(data.status)} size="small" />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}><Typography variant="caption" color="text.secondary">Total Pages: {usage.totalAvailable.toLocaleString()} pages</Typography></Box>
                {nextResetDate && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><EventAvailableIcon sx={{ fontSize: 14 }} />{data.isPaidStripeSubscription ? (data.autoRenew ? 'Renews on' : 'Ends on') : 'Expires on'} {new Date(nextResetDate).toLocaleDateString()}</Typography>
                    {daysUntilReset !== null && <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, color: daysUntilReset <= 3 ? theme.palette.warning.main : 'text.secondary' }}><AccessTimeIcon sx={{ fontSize: 12 }} />{resetMessage}</Typography>}
                    {subscriptionStartDate && <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>Started: {new Date(subscriptionStartDate).toLocaleDateString()}</Typography>}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>ℹ️ Included plan pages expire at the end of their trial/subscription period and do not carry over. Purchased extra pages remain available until used.</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'stretch', md: 'flex-end' },
                gap: 1.25
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'stretch', sm: 'flex-end' },
                  gap: 1,
                  width: { xs: '100%', md: 350 },
                  '& .MuiButton-root': { minHeight: 40 }
                }}
              >
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={isAtMaxPlan ? <CheckCircleIcon /> : <RocketLaunchIcon />}
                  onClick={handleUpgradePlan}
                  disabled={checkoutLoading !== null || (hasActiveSubscription && isAtMaxPlan)}
                  sx={{
                    flex: 1.5,
                    whiteSpace: 'nowrap',
                    background: hasActiveSubscription && isAtMaxPlan
                      ? alpha(theme.palette.success.main, 0.5)
                      : `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.light} 100%)`,
                    color: '#ffffff'
                  }}
                >
                  {checkoutLoading === "upgrade"
                    ? "Processing..."
                    : hasActiveSubscription && isAtMaxPlan
                      ? "Maximum Plan"
                      : !hasActiveSubscription
                        ? "View Plans"
                        : "Upgrade Plan"}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{ flex: 1, whiteSpace: 'nowrap' }}
                >
                  Refresh
                </Button>
              </Box>

              {data.isPaidStripeSubscription && hasActiveSubscription && (
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: 2.5,
                    width: { xs: '100%', md: 350 },
                    bgcolor: alpha(theme.palette.background.paper, 0.82),
                    borderColor: alpha(data.autoRenew ? theme.palette.success.main : theme.palette.warning.main, 0.28)
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                        {data.autoRenew
                          ? <AutorenewIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                          : <EventBusyIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />}
                        <Typography variant="body2" fontWeight={800}>Subscription Renewal</Typography>
                        <Chip
                          size="small"
                          label={data.autoRenew ? "Enabled" : "Disabled"}
                          sx={{
                            ml: 'auto',
                            height: 23,
                            fontWeight: 800,
                            bgcolor: alpha(data.autoRenew ? theme.palette.success.main : theme.palette.warning.main, 0.14),
                            color: data.autoRenew ? theme.palette.success.dark : theme.palette.warning.dark
                          }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.25 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          {data.autoRenew ? 'Next renewal' : 'Ends'}
                        </Typography>
                        <Typography variant="body2" fontWeight={800}>
                          {data.stripePeriodEnd
                            ? new Date(data.stripePeriodEnd).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
                            : 'End of current period'}
                        </Typography>
                      </Box>

                      <Typography variant="caption" color="text.secondary" display="block">
                        {data.autoRenew
                          ? 'Your saved payment method will be charged automatically.'
                          : 'No further payments will be taken.'}
                      </Typography>

                      <Button
                        size="small"
                        variant="text"
                        startIcon={<HelpOutlineIcon sx={{ fontSize: 17 }} />}
                        onClick={() => {
                          setPopupSeverity('info');
                          setPopupMessage('Auto-Renew keeps your subscription active by charging your saved payment method at the end of each billing period. Turning it off does not cancel your current paid access.');
                          setPopupOpen(true);
                        }}
                        sx={{ mt: 0.25, px: 0, py: 0.25, minHeight: 28, textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
                      >
                        How Auto-Renew works
                      </Button>
                    </Box>

                    <Switch
                      checked={Boolean(data.autoRenew)}
                      onChange={(_, checked) => requestAutoRenewChange(checked)}
                      disabled={autoRenewUpdating || data.stripeSubscriptionStatus === 'canceled'}
                      inputProps={{ 'aria-label': 'Subscription Auto-Renew' }}
                      sx={{
                        mt: 0.1,
                        mr: -0.5,
                        '& .MuiSwitch-switchBase.Mui-checked': { color: theme.palette.success.main },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: theme.palette.success.main },
                        '& .MuiSwitch-track': { backgroundColor: theme.palette.warning.main }
                      }}
                    />
                  </Box>
                </Paper>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {hasActiveSubscription && isYearlyPlan && period === 'monthly' && <Alert severity="info" sx={{ mb: 3 }} icon={<EventBusyIcon />}><Box><Typography variant="body2" fontWeight={600}>Yearly Plan Commitment</Typography><Typography variant="caption">You're on a yearly plan. Monthly plans will be available at renewal on {renewalDate?.toLocaleDateString()}.</Typography></Box></Alert>}
      {pageBanner && <Alert severity={pageBanner.severity} sx={{ mb: 3 }} onClose={() => setPageBanner(null)}>{pageBanner.message}</Alert>}
      {lowQuotaWarning && <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningIcon />} action={<Button color="inherit" size="small" onClick={() => planSectionRef.current?.scrollIntoView({ behavior: "smooth" })}>View Plans</Button>}>You're approaching your page limit ({Math.round(usage.percent)}% used). Consider upgrading.</Alert>}
      {exhaustedQuota && <Alert severity="error" sx={{ mb: 3 }} action={<Button color="inherit" size="small" onClick={() => planSectionRef.current?.scrollIntoView({ behavior: "smooth" })}>Buy Now</Button>}>You've exhausted your page quota. Purchase extra pages to continue.</Alert>}
      {!data.isActive && (
        <Alert severity={usage.extraPages > 0 ? "info" : "warning"} sx={{ mb: 3 }}>
          {usage.extraPages > 0
            ? `Your plan has expired. You still have ${usage.extraPages.toLocaleString()} purchased extra pages preserved on your account. Activate a new subscription plan below to continue processing and use your remaining pages.`
            : "Your plan has expired. Select a new subscription plan below to reactivate processing."}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><StatCard label="Plan Limit" value={usage.baseQuota} helper={`Included in ${isYearlyPlan ? 'yearly' : 'monthly'} plan`} icon={<TrendingUpIcon />} /></Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><StatCard label="Extra Pages" value={usage.extraPages} helper={usage.extraPages > 0 ? "Purchased pages remain until used" : "No purchased extra pages"} emphasis={usage.extraPages > 0} /></Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><StatCard label="Total Available" value={usage.totalAvailable} helper="Base quota + extra pages" emphasis /></Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><StatCard label="Remaining" value={usage.remaining} helper={`${Math.round(usage.percent)}% used`} emphasis /></Grid>
      </Grid>

      <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>Usage Overview</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1 }}><LinearProgress variant="determinate" value={usage.percent} sx={{ height: 12, borderRadius: 6, backgroundColor: alpha(theme.palette.success.main, 0.1), '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg, ${progressColor} 0%, ${progressLightColor} 100%)`, borderRadius: 6 } }} /></Box>
          <Typography variant="h6" fontWeight={800}>{Math.round(usage.percent)}%</Typography>
        </Box>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={{ xs: 3 }}><Typography variant="caption" color="text.secondary">Used</Typography><Typography variant="body2" fontWeight={600}>{usage.used.toLocaleString()}</Typography></Grid>
          <Grid size={{ xs: 3 }}><Typography variant="caption" color="text.secondary">Reserved</Typography><Typography variant="body2" fontWeight={600}>{usage.reserved.toLocaleString()}</Typography></Grid>
          <Grid size={{ xs: 3 }}><Typography variant="caption" color="text.secondary">Remaining</Typography><Typography variant="body2" fontWeight={600}>{usage.remaining.toLocaleString()}</Typography></Grid>
          <Grid size={{ xs: 3 }}><Typography variant="caption" color="text.secondary">Total</Typography><Typography variant="body2" fontWeight={600}>{usage.totalAvailable.toLocaleString()}</Typography></Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>Add Extra Pages</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Purchase additional pages for an active subscription. Any unused purchased extra pages are preserved if your subscription expires and become usable again after you activate a new subscription.
        </Typography>
        {!hasActiveSubscription && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={700}>Active subscription required</Typography>
            <Typography variant="caption">
              Your existing purchased extra pages are preserved, but additional top-ups can only be purchased while a subscription is active. Select a subscription plan below to reactivate your account.
            </Typography>
          </Alert>
        )}
        <Grid container spacing={2}>
          {EXTRA_PAGES_OPTIONS.map((option) => (
            <Grid size={{ xs: 12, sm: 4 }} key={option.pages}>
              <Card sx={{ borderRadius: 2, border: `1px solid ${mode === 'light' ? 'rgba(10, 37, 64, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`, transition: 'all 0.2s', opacity: hasActiveSubscription ? 1 : 0.6, '&:hover': hasActiveSubscription ? { transform: 'translateY(-2px)', borderColor: theme.palette.secondary.main, boxShadow: theme.shadows[4] } : {} }}>
                <CardContent sx={{ textAlign: 'center' }}><Typography variant="h4" fontWeight={800} color="primary" gutterBottom>${option.price}</Typography><Typography variant="h6" fontWeight={600}>{option.pages} Pages</Typography><Typography variant="caption" color="text.secondary">${(option.price / option.pages).toFixed(2)} per page</Typography></CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Tooltip title={!hasActiveSubscription ? "Activate a subscription before purchasing extra pages" : ""} arrow disableHoverListener={hasActiveSubscription}>
                    <span style={{ width: '100%' }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => handleBuyExtraPages(option.pages)}
                        disabled={!hasActiveSubscription || checkoutLoading !== null}
                        startIcon={<AddShoppingCartIcon />}
                        sx={{ borderColor: theme.palette.secondary.main, color: theme.palette.secondary.main, '&:hover': { borderColor: theme.palette.secondary.dark, bgcolor: alpha(theme.palette.secondary.main, 0.05) } }}
                      >
                        {checkoutLoading === option.pages ? "Processing..." : "Buy Now"}
                      </Button>
                    </span>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Box ref={planSectionRef}>
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight={700}>{hasActiveSubscription ? "Change or Upgrade Plan" : "Choose a Subscription Plan"}</Typography>
            <ToggleButtonGroup 
              value={period} 
              exclusive 
              sx={{ '& .MuiToggleButton-root': { px: 4, py: 1.5, fontSize: '1.1rem', fontWeight: 600, border: '2px solid transparent', '&.Mui-selected': { backgroundColor: alpha(theme.palette.secondary.main, 0.15), color: theme.palette.secondary.main, borderColor: theme.palette.secondary.main, borderWidth: '2px' }, '&:first-of-type': { borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }, '&:last-of-type': { borderTopRightRadius: 12, borderBottomRightRadius: 12 } } }}
            >
              <ToggleButton 
                value="monthly" 
                onClick={() => {
                  if (hasActiveSubscription && isYearlyPeriod(data?.period)) {
                    setPopupMessage(`You are on a yearly plan. Monthly plans will be available for selection after your current plan expires on ${new Date(nextResetDate || '').toLocaleDateString()}.`);
                    setPopupSeverity("info");
                    setPopupOpen(true);
                    return;
                  }
                  setPeriod('monthly');
                }}
                sx={{ 
                  opacity: hasActiveSubscription && isYearlyPeriod(data?.period) ? 0.5 : 1,
                  cursor: 'pointer',
                  position: 'relative'
                }}
              >
                📅 Monthly
              </ToggleButton>
              <ToggleButton 
                value="yearly"
                onClick={() => setPeriod('yearly')}
                sx={{
                  position: 'relative',
                  '&::after': {
                    content: '"Save 25%"',
                    position: 'absolute',
                    top: -12,
                    right: -8,
                    backgroundColor: theme.palette.secondary.main,
                    color: '#ffffff',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    padding: '2px 6px',
                    borderRadius: 1,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.secondary.main, 0.3)}`,
                  }
                }}
              >
                📆 Yearly
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Grid container spacing={3}>{(period === 'monthly' ? MONTHLY_PLANS : YEARLY_PLANS).map((plan) => {
            const isCurrentPlan = (() => { if (!data || !hasActiveSubscription) return false; if (basePlanCode) return basePlanCode === plan.code; const currentPeriodType = data.period; const currentMonthlyLimit = data.monthlyLimit || 0; if (period === 'monthly') return isMonthlyPeriod(currentPeriodType) && currentMonthlyLimit >= plan.pages && currentMonthlyLimit < (getNextPlanPages(plan.code) || Infinity); if (period === 'yearly') return isYearlyPeriod(currentPeriodType) && currentMonthlyLimit >= plan.pages && currentMonthlyLimit < (getNextPlanPages(plan.code) || Infinity); return false; })();
            const isSameTier = (() => { if (!data || !hasActiveSubscription) return false; if (isCurrentPlan) return false; const currentPlanValue = data.plan; const getTierFromCode = (code: string): number => { if (code.includes('STARTER')) return 501900000; if (code.includes('BUSINESS')) return 501900001; if (code.includes('ENTERPRISE')) return 501900002; return 0; }; const planTier = getTierFromCode(plan.code); const currentTier = typeof currentPlanValue === 'string' ? parseInt(currentPlanValue, 10) : currentPlanValue; return planTier === currentTier && data.monthlyLimit === plan.pages; })();
            const isYearlyToMonthly = hasActiveSubscription && isYearlyPeriod(data?.period) && period === 'monthly' && !isSameTier;
            const isDifferentTier = (() => { if (!data || !hasActiveSubscription) return false; if (isCurrentPlan) return false; if (isSameTier) return false; const currentPlanValue = data.plan; const getTierFromCode = (code: string): number => { if (code.includes('STARTER')) return 501900000; if (code.includes('BUSINESS')) return 501900001; if (code.includes('ENTERPRISE')) return 501900002; return 0; }; const planTier = getTierFromCode(plan.code); return planTier !== currentPlanValue; })();
            
            // FIX: Use handlePlanSelect instead of startPlanCheckout directly
            return <Grid size={{ xs: 12, sm: 6, md: 4 }} key={plan.code}><PlanCard 
              plan={plan} 
              isCurrentPlan={isCurrentPlan} 
              isSameTier={isSameTier} 
              isYearlyToMonthly={isYearlyToMonthly} 
              isDifferentTier={isDifferentTier} 
              onSelect={() => { 
                if (isYearlyToMonthly) { 
                  setPopupMessage(`Your yearly plan is active until ${renewalDate?.toLocaleDateString() || 'your renewal date'}. Monthly plans will be available at renewal.`); 
                  setPopupSeverity("info"); 
                  setPopupOpen(true); 
                } else { 
                  handlePlanSelect(plan.code); 
                } 
              }} 
              loading={checkoutLoading === plan.code} 
              period={period} 
              disabled={isYearlyToMonthly} 
              disabledReason={isYearlyToMonthly ? "Yearly plans cannot switch to monthly until renewal" : undefined} 
            /></Grid>;
          })}</Grid>
        </Paper>
      </Box>

      <Dialog
        open={autoRenewConfirmOpen}
        onClose={() => {
          if (!autoRenewUpdating) {
            setAutoRenewConfirmOpen(false);
            setPendingAutoRenewValue(null);
          }
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {pendingAutoRenewValue ? 'Enable Auto-Renew?' : 'Disable Auto-Renew?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {pendingAutoRenewValue
              ? 'Your subscription will renew automatically using your saved payment method.'
              : 'Your subscription will remain active until the end of the current paid period, then it will expire.'}
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(pendingAutoRenewValue ? theme.palette.success.main : theme.palette.warning.main, 0.06),
              borderColor: alpha(pendingAutoRenewValue ? theme.palette.success.main : theme.palette.warning.main, 0.25)
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block">
              {pendingAutoRenewValue ? 'Next renewal' : 'Access remains active until'}
            </Typography>
            <Typography variant="body1" fontWeight={800}>
              {data?.stripePeriodEnd
                ? new Date(data.stripePeriodEnd).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
                : 'End of current billing period'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {pendingAutoRenewValue
                ? 'Your saved payment method will be charged on this date.'
                : 'No further payments will be taken after you confirm.'}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => {
              setAutoRenewConfirmOpen(false);
              setPendingAutoRenewValue(null);
            }}
            disabled={autoRenewUpdating}
          >
            Keep current setting
          </Button>
          <Button
            variant="contained"
            color={pendingAutoRenewValue ? 'success' : 'warning'}
            onClick={handleAutoRenewChange}
            disabled={autoRenewUpdating}
          >
            {autoRenewUpdating
              ? 'Updating...'
              : pendingAutoRenewValue
                ? 'Enable Auto-Renew'
                : 'Disable Auto-Renew'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={upgradeDialogOpen} onClose={() => checkoutLoading !== selectedUpgradePlan?.code && setUpgradeDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>Confirm your plan upgrade</DialogTitle>
        <DialogContent>
          {upgradePreviewLoading ? (
            <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>
          ) : upgradePreview && selectedUpgradePlan ? (
            <Box>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                You're upgrading from <strong>{getFullPlanTitle(upgradePreview.currentPlanCode)}</strong> to <strong>{selectedUpgradePlan.title}</strong>. Review the payment details before continuing.
              </Typography>

              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5 }}>
                <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1.5 }}>
                  Today's upgrade
                </Typography>

                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                  <Typography color="text.secondary">{selectedUpgradePlan.title} (remaining period)</Typography>
                  <Typography fontWeight={700}>
                    {new Intl.NumberFormat(undefined, { style: "currency", currency: upgradePreview.currency }).format(upgradePreview.newPlanProratedCharge / 100)}
                  </Typography>
                </Box>

                {upgradePreview.currentPlanCredit > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 1 }}>
                    <Typography color="text.secondary">Credit from your {getFullPlanTitle(upgradePreview.currentPlanCode)} plan</Typography>
                    <Typography fontWeight={700} color="success.main">
                      -{new Intl.NumberFormat(undefined, { style: "currency", currency: upgradePreview.currency }).format(upgradePreview.currentPlanCredit / 100)}
                    </Typography>
                  </Box>
                )}

                {upgradePreview.taxAmount > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 1 }}>
                    <Typography color="text.secondary">Tax</Typography>
                    <Typography fontWeight={700}>
                      {new Intl.NumberFormat(undefined, { style: "currency", currency: upgradePreview.currency }).format(upgradePreview.taxAmount / 100)}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center" }}>
                  <Typography fontWeight={800}>Today's payment</Typography>
                  <Typography variant="h6" fontWeight={900} color="primary">
                    {new Intl.NumberFormat(undefined, { style: "currency", currency: upgradePreview.currency }).format(upgradePreview.amountDue / 100)}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
                  You're only paying the prorated difference for the remainder of your current billing period.
                </Typography>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2.5, mt: 2, borderRadius: 2.5 }}>
                <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1.5 }}>
                  What changes after payment
                </Typography>

                <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                  <CheckCircleIcon color="success" fontSize="small" sx={{ mt: 0.2 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={700}>Page allowance</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {(data?.monthlyLimit || 0).toLocaleString()} → {upgradePreview.pageLimit.toLocaleString()} pages. Your existing usage is preserved.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", mt: 1.5 }}>
                  <CheckCircleIcon color="success" fontSize="small" sx={{ mt: 0.2 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={700}>Future renewals</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {new Intl.NumberFormat(undefined, { style: "currency", currency: upgradePreview.currency }).format(upgradePreview.nextRecurringAmount / 100)} / {upgradePreview.billingInterval || "period"}
                      {upgradePreview.nextRenewalDate ? `, next charged on ${new Date(upgradePreview.nextRenewalDate).toLocaleDateString()}.` : "."}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", mt: 1.5 }}>
                  <CheckCircleIcon color="success" fontSize="small" sx={{ mt: 0.2 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={700}>Auto-Renew</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {upgradePreview.autoRenew
                        ? "Remains enabled. Your subscription will renew automatically on the date shown above."
                        : `Remains disabled. Your upgraded plan stays active until ${upgradePreview.nextRenewalDate ? new Date(upgradePreview.nextRenewalDate).toLocaleDateString() : "the end of the current paid period"} and will not renew automatically.`}
                    </Typography>
                  </Box>
                </Box>

                {upgradePreview.paymentMethod && (
                  <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", mt: 1.5 }}>
                    <SecurityIcon color="primary" fontSize="small" sx={{ mt: 0.2 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={700}>Payment method</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ textTransform: "capitalize" }}>
                        {upgradePreview.paymentMethod.brand} •••• {upgradePreview.paymentMethod.last4}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Paper>

              <Alert severity="info" sx={{ mt: 2 }}>
                Your current plan remains active until Stripe confirms payment. If bank authentication is required, you'll be redirected securely to complete it.
              </Alert>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setUpgradeDialogOpen(false)}
            disabled={checkoutLoading === selectedUpgradePlan?.code}
          >
            Keep current plan
          </Button>
          <Button
            variant="contained"
            onClick={confirmPlanChange}
            disabled={!upgradePreview || checkoutLoading === selectedUpgradePlan?.code}
          >
            {checkoutLoading === selectedUpgradePlan?.code
              ? "Processing..."
              : upgradePreview
                ? upgradePreview.amountDue > 0
                  ? `Confirm and Pay ${new Intl.NumberFormat(undefined, { style: "currency", currency: upgradePreview.currency }).format(upgradePreview.amountDue / 100)}`
                  : "Confirm Upgrade"
                : "Confirm Upgrade"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={popupOpen} autoHideDuration={5000} onClose={() => setPopupOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} sx={{ mt: { xs: 4, sm: 8, md: 10 } }}><Alert onClose={() => setPopupOpen(false)} severity={popupSeverity} variant="filled" sx={{ minWidth: { xs: '300px', sm: '400px', md: '500px' }, maxWidth: '600px', mx: 2, borderRadius: { xs: 2, sm: 3, md: 4 }, backdropFilter: 'blur(16px)', backgroundColor: { info: alpha(theme.palette.info.main, 0.75), success: alpha(theme.palette.success.main, 0.75), warning: alpha(theme.palette.warning.main, 0.75), error: alpha(theme.palette.error.main, 0.75) }[popupSeverity], color: '#ffffff', boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.25)}`, border: `1px solid ${alpha(theme.palette.common.white, 0.15)}` }}>{popupMessage}</Alert></Snackbar>
    </Container>
  );
};

<Typography variant="caption">
Build: 28 Jul 2026 - expired-account billing UI update
</Typography>

export default SubscriptionPage;